// Port of Services/SBotChatTitleGenerator.swift. Generation runs server-side
// (POST /ai/title — iOS uses on-device Apple Intelligence instead); the
// sanitizer is a faithful port of SBotChatTitleSanitizer.

import { apiRequest } from "@/services/functionsClient";
import type { SBotChatMessage } from "@/models/aiChat";

export const MAX_TITLE_LENGTH = 48;

export async function generateSmartTitle(userMessage: SBotChatMessage, assistantMessage: SBotChatMessage): Promise<string | null> {
  const response = await apiRequest<{ title: string }>("/ai/title", {
    body: {
      userText: excerpt(userMessage),
      assistantText: excerpt(assistantMessage),
    },
  });
  return sanitizedTitle(response.title);
}

function excerpt(message: SBotChatMessage, limit = 1_500): string {
  const trimmed = message.content.trim();
  if (trimmed.length > 0) return trimmed.slice(0, limit);
  if (message.attachments.length === 0) return "No text content.";
  return `Attachments: ${message.attachments.map((attachment) => attachment.name).join(", ")}`;
}

export function sanitizedTitle(rawTitle: string): string | null {
  let title = rawTitle.replace(/[\n\r]/g, " ").trim();

  title = stripPrefix(title);
  title = title.split(/\s+/).filter(Boolean).join(" ");
  title = stripWrappingQuotes(title).trim();

  while (title.endsWith(".") || title.endsWith(":") || title.endsWith(";")) {
    title = title.slice(0, -1).trim();
  }

  if (title.length === 0) return null;

  if (title.length > MAX_TITLE_LENGTH) {
    title = title.slice(0, MAX_TITLE_LENGTH).trim();
    while (title.endsWith(",") || title.endsWith("-") || title.endsWith(":") || title.endsWith(";")) {
      title = title.slice(0, -1).trim();
    }
  }

  return title.length > 0 ? title : null;
}

function stripPrefix(title: string): string {
  const lowercased = title.toLowerCase();
  for (const prefix of ["title:", "chat title:"]) {
    if (lowercased.startsWith(prefix)) {
      return title.slice(prefix.length).trim();
    }
  }
  return title;
}

function stripWrappingQuotes(title: string): string {
  const pairs: Array<[string, string]> = [
    ['"', '"'],
    ["'", "'"],
    ["“", "”"],
    ["‘", "’"],
  ];
  if (title.length < 2) return title;
  for (const [opening, closing] of pairs) {
    if (title.startsWith(opening) && title.endsWith(closing)) {
      return title.slice(1, -1);
    }
  }
  return title;
}
